import type { LucideIcon } from 'lucide-react'
import type { EntryKind, TagKind } from '@/types'

import {
    BookOpen,
    CalendarClock,
    CalendarDays,
    Compass,
    Crown,
    Eye,
    Hexagon,
    Layers,
    ListOrdered,
    Package,
    RotateCcw,
    ScanSearch,
    Sparkles,
    Target,
    TrendingUp,
    Zap
} from 'lucide-react'

export interface EntryKindMeta {
    kind: EntryKind
    label: string
    plural: string
    hindi: string
    description: string
    icon: LucideIcon
    tone: string
}

export interface TagKindMeta {
    kind: TagKind
    label: string
    singular: string
    path: string
    description: string
    icon: LucideIcon
    tone: string
}

export const ENTRY_KINDS: EntryKindMeta[] = [
    { kind: 'brahmastra', label: 'Brahmastra', plural: 'Brahmastra', hindi: 'ब्रह्मास्त्र', description: 'Master remedies that work across almost every problem.', icon: Zap, tone: 'bg-brand-rose' },
    { kind: 'remedy', label: 'Remedy', plural: 'Remedies', hindi: 'उपाय', description: 'Upay that fix a specific problem or dosh.', icon: Sparkles, tone: 'bg-brand-green' },
    { kind: 'method', label: 'Method', plural: 'Methods and Vidhi', hindi: 'विधि', description: 'Step-by-step rituals, purification and procedures.', icon: ListOrdered, tone: 'bg-brand-blue' },
    { kind: 'boosting', label: 'Energy Boosting', plural: 'Energy Boosting', hindi: 'ऊर्जा वृद्धि', description: 'Ways to raise and activate land, door and zone energy.', icon: TrendingUp, tone: 'bg-brand-yellow' },
    { kind: 'usara', label: 'Usara', plural: 'Usara Methods', hindi: 'उसारा', description: 'Usara and utara rituals that remove nazar and negativity.', icon: RotateCcw, tone: 'bg-brand-violet' },
    { kind: 'muhurta', label: 'Muhurta', plural: 'Muhurta Rules', hindi: 'मुहूर्त', description: 'When to act: tithi, vaar, nakshatra, hora and swar timing.', icon: CalendarClock, tone: 'bg-brand-orange' },
    { kind: 'diagnosis', label: 'Testing', plural: 'Testing and Diagnosis', hindi: 'परीक्षण', description: 'Land energy tests, dowsing, swar and tatva identification.', icon: ScanSearch, tone: 'bg-brand-cyan' },
    { kind: 'principle', label: 'Principle', plural: 'Principles and Rules', hindi: 'सिद्धांत', description: 'Concepts, rules, dos and donts behind every remedy.', icon: BookOpen, tone: 'bg-brand-stone' },
    { kind: 'mantra', label: 'Mantra', plural: 'Mantras and Yantras', hindi: 'मंत्र', description: 'Mantras and yantras and how to use them.', icon: Hexagon, tone: 'bg-brand-pink' },
    { kind: 'devta', label: 'Vastu Devta', plural: 'Vastu Devtas', hindi: 'वास्तु देवता', description: 'The 45 devtas: zone, nature, samagri and remedies.', icon: Crown, tone: 'bg-brand-amber' },
    { kind: 'prediction', label: 'Prediction', plural: 'Forecasting Techniques', hindi: 'भविष्यवाणी', description: 'Omens, nimitta, dreams and swar based prediction.', icon: Eye, tone: 'bg-brand-indigo' }
]

const FALLBACK_KIND: EntryKindMeta = {
    kind: 'principle',
    label: 'Knowledge',
    plural: 'Knowledge',
    hindi: 'ज्ञान',
    description: 'Knowledge from the courses.',
    icon: BookOpen,
    tone: 'bg-brand-stone'
}

export const entryKindMeta = (kind: string | null | undefined): EntryKindMeta => {
    return ENTRY_KINDS.find(item => item.kind === kind) ?? FALLBACK_KIND
}

export const isEntryKind = (value: unknown): value is EntryKind => {
    return typeof value === 'string' && ENTRY_KINDS.some(item => item.kind === value)
}

export const TAG_KINDS: Record<TagKind, TagKindMeta> = {
    samagri: { kind: 'samagri', label: 'Samagri', singular: 'Samagri', path: '/samagri', description: 'Every item and material, with every remedy and method it is used in.', icon: Package, tone: 'bg-brand-yellow' },
    event: { kind: 'event', label: 'Events and Occasions', singular: 'Event', path: '/events', description: 'Purchases, ceremonies, construction, festivals and daily life.', icon: CalendarDays, tone: 'bg-brand-orange' },
    topic: { kind: 'topic', label: 'Topics', singular: 'Topic', path: '/topics', description: 'Ghar, business, aay and industrial vastu, swar, muhurta and more.', icon: Layers, tone: 'bg-brand-blue' },
    place: { kind: 'place', label: 'Places and Directions', singular: 'Place', path: '/places', description: 'The directions, the 16 zones, rooms and structures.', icon: Compass, tone: 'bg-brand-green' },
    goal: { kind: 'goal', label: 'Goals and Problems', singular: 'Goal', path: '/goals', description: 'Wealth, health, marriage, protection, education and more.', icon: Target, tone: 'bg-brand-pink' }
}

export const TAG_KIND_ORDER: TagKind[] = ['samagri', 'event', 'place', 'goal', 'topic']

export const tagHref = (kind: string, slug: string): string => {
    const meta = TAG_KINDS[kind as TagKind]

    return meta ? `${meta.path}/${slug}` : `/search?q=${encodeURIComponent(slug.replace(/-/g, ' '))}`
}

const CATEGORY_LABELS: Record<string, string> = {
    'seeds-grains': 'Seeds and Grains',
    'spices-herbs': 'Spices and Herbs',
    fragrance: 'Incense and Fragrance',
    'ritual-items': 'Ritual Items',
    'plants-leaves': 'Plants, Trees and Leaves',
    food: 'Food Items',
    'minerals-stones': 'Minerals, Shells and Stones',
    metals: 'Metals',
    liquids: 'Liquids',
    'animal-products': 'Cow and Animal Products',
    'cloth-thread': 'Cloth and Thread',
    objects: 'Objects and Decor',
    'symbols-yantras': 'Symbols, Yantras and Pyramids',
    tools: 'Tools',
    'life-event': 'Life Events and Purchases',
    ceremony: 'Ceremonies and Construction',
    festival: 'Festivals and Special Days',
    daily: 'Daily Life',
    direction: 'Main Directions',
    zone: 'The 16 Zones',
    room: 'Rooms and Spaces',
    structure: 'Structures',
    vastu: 'Vastu',
    swar: 'Swar Vigyan',
    muhurta: 'Muhurta',
    forecasting: 'Forecasting',
    jyotish: 'Jyotish and Numerology',
    goal: 'Goals'
}

export const CATEGORY_ORDER: Record<TagKind, string[]> = {
    samagri: ['seeds-grains', 'spices-herbs', 'minerals-stones', 'metals', 'symbols-yantras', 'ritual-items', 'plants-leaves', 'fragrance', 'liquids', 'animal-products', 'food', 'cloth-thread', 'objects', 'tools'],
    event: ['life-event', 'ceremony', 'festival', 'daily'],
    topic: ['vastu', 'swar', 'muhurta', 'forecasting', 'jyotish'],
    place: ['direction', 'zone', 'room', 'structure'],
    goal: ['goal']
}

export const categoryLabel = (category: string | null | undefined): string => {
    if (!category) return 'Other'

    return CATEGORY_LABELS[category] ?? category.split('-').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ')
}

const FORMAT_LABELS: Record<string, string> = {
    class: 'Class',
    revision: 'Revision',
    'doubt-session': 'Doubt Session',
    'live-qa': 'Live Q&A',
    webinar: 'Webinar',
    talk: 'Talk',
    episode: 'Episode',
    short: 'Short Tip',
    pooja: 'Live Pooja'
}

export const formatLabel = (format: string | null | undefined): string => {
    if (!format) return 'Session'

    return FORMAT_LABELS[format] ?? format.charAt(0).toUpperCase() + format.slice(1)
}

const SUBJECT_TONES: Record<string, string> = {
    vastu: 'bg-brand-yellow',
    swar: 'bg-brand-blue',
    muhurta: 'bg-brand-orange',
    forecasting: 'bg-brand-violet',
    videos: 'bg-brand-pink'
}

export const subjectTone = (subject: string | null | undefined): string => {
    return SUBJECT_TONES[subject ?? ''] ?? 'bg-brand-green'
}
